import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, Activity, HelpCircle, FileText, Sparkles } from "lucide-react";
import evgatewayLogo from "@/assets/evgateway-logo-transparent.png";

// Announcement banner configuration - easy to update
const announcement = {
  enabled: true,
  text: "🚀 Launching new 4.0 Mobile Apps",
  link: "/news",
  isExternal: false,
};

const navLinks = [
  { name: "Solutions", href: "/solutions" },
  { name: "Products", href: "/products" },
  { name: "CPO Partners", href: "/cpo" },
  { name: "Drivers", href: "/drivers" },
  { 
    name: "Company", 
    href: "#",
    children: [
      { name: "About Us", href: "/about" },
      { name: "News & Media", href: "/news" },
      { name: "Contact Us", href: "/contact" },
    ]
  },
  { 
    name: "Resources", 
    href: "#",
    children: [
      { name: "System Status", href: "https://evg-status.evgateway.com/", external: true, icon: "status" },
      { name: "Support", href: "/support", icon: "help" },
      { name: "Documentation", href: "/products#features", icon: "docs" },
    ]
  },
];

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDropdownToggle = (linkName: string) => {
    setOpenDropdown(openDropdown === linkName ? null : linkName);
  };

  const handleLinkClick = () => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const renderNavLink = (link: typeof navLinks[0], index: number) => {
    if (link.children) {
      return (
        <div key={link.name} className="relative">
          <button
            onClick={() => handleDropdownToggle(link.name)}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
          >
            {link.name}
            <ChevronDown className={`h-4 w-4 transition-transform ${openDropdown === link.name ? 'rotate-180' : ''}`} />
          </button>
          
          {openDropdown === link.name && (
            <div className="absolute top-full left-0 mt-2 py-2 w-44 rounded-xl bg-card border border-border shadow-xl animate-fade-in z-50">
              {link.children.map((child) => {
                const IconComponent = child.icon === 'status' ? Activity : child.icon === 'help' ? HelpCircle : child.icon === 'docs' ? FileText : null;
                
                // External links open in new tab
                if (child.external) {
                  return (
                    <a
                      key={child.name}
                      href={child.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(null);
                      }}
                    >
                      <span className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="h-4 w-4" />}
                        {child.name}
                      </span>
                    </a>
                  );
                }
                
                // Hash links (e.g., /products#features) should use anchor tags
                if (child.href.includes('#')) {
                  return (
                    <a
                      key={child.name}
                      href={child.href}
                      className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(null);
                      }}
                    >
                      <span className="flex items-center gap-2">
                        {IconComponent && <IconComponent className="h-4 w-4" />}
                        {child.name}
                      </span>
                    </a>
                  );
                }
                
                // Internal links use React Router
                return (
                  <Link
                    key={child.name}
                    to={child.href}
                    className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDropdown(null);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      {IconComponent && <IconComponent className="h-4 w-4" />}
                      {child.name}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    if (link.href.startsWith("/") && !link.href.includes("#")) {
      return (
        <Link
          key={link.name}
          to={link.href}
          className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
        >
          {link.name}
        </Link>
      );
    }

    return (
      <a
        key={link.name}
        href={link.href}
        className="text-muted-foreground hover:text-foreground transition-colors text-sm font-medium"
      >
        {link.name}
      </a>
    );
  };

  return (
    <>
      {/* Announcement Banner */}
      {announcement.enabled && (
        <div 
          className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${
            isScrolled ? "opacity-0 pointer-events-none -translate-y-full" : "opacity-100"
          }`}
        >
          {announcement.isExternal ? (
            <a 
              href={announcement.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-2 text-center text-sm font-medium text-white hover:opacity-90 transition-opacity"
              style={{
                background: 'linear-gradient(90deg, hsl(217, 91%, 50%) 0%, hsl(199, 89%, 48%) 50%, hsl(172, 66%, 50%) 100%)',
              }}
            >
              <span className="flex items-center justify-center gap-2">
                <Sparkles className="h-4 w-4" />
                {announcement.text}
                <span className="hidden sm:inline opacity-80">→ Learn more</span>
              </span>
            </a>
          ) : (
            <Link 
              to={announcement.link}
              className="block w-full py-2 text-center text-sm font-medium text-white hover:opacity-90 transition-opacity"
              style={{
                background: 'linear-gradient(90deg, hsl(217, 91%, 50%) 0%, hsl(199, 89%, 48%) 50%, hsl(172, 66%, 50%) 100%)',
              }}
            >
              <span className="flex items-center justify-center gap-2">
                <Sparkles className="h-4 w-4" />
                {announcement.text}
                <span className="hidden sm:inline opacity-80">→ Learn more</span>
              </span>
            </Link>
          )}
        </div>
      )}
      
      <header
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "glass-strong py-3 top-0" : `bg-transparent py-5 ${announcement.enabled ? "top-9" : "top-0"}`
        }`}
      >
      <nav className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <img 
            src={evgatewayLogo} 
            alt="EVGateway"
            width={241}
            height={56}
            fetchPriority="high"
            className={`w-auto transition-all duration-300 group-hover:scale-105 ${
              isScrolled ? "h-10 logo-glow-pulse-scrolled" : "h-14 logo-glow-pulse"
            }`}
          />
        </Link>

        {/* Desktop Navigation */}
        <div ref={navRef} className="hidden lg:flex items-center gap-8">
          {navLinks.map((link, index) => renderNavLink(link, index))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <a href="https://portal.evgateway.com/#/login" target="_blank" rel="noopener noreferrer">
            <Button variant="ghost" size="sm" className="text-foreground hover:text-foreground/80">
              Sign In
            </Button>
          </a>
          <a href="https://portal.evgateway.com/#/joinus" target="_blank" rel="noopener noreferrer">
            <Button variant="hero" size="sm">
              Register
            </Button>
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden text-foreground p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden mt-2 mx-4 rounded-xl p-4 animate-fade-in relative z-[100] bg-[hsl(223,80%,10%)] border border-border/60 shadow-2xl"
          style={{ pointerEvents: 'auto' }}
        >
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => {
              if (link.children) {
                return (
                  <div key={link.name} className="border-b border-border/30 last:border-b-0">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenDropdown(openDropdown === link.name ? null : link.name);
                      }}
                      className="flex items-center justify-between w-full text-muted-foreground hover:text-foreground transition-colors py-4 min-h-[52px] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <span className="text-base font-medium">{link.name}</span>
                      <ChevronDown className={`h-5 w-5 transition-transform ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === link.name && (
                      <div 
                        className="pl-2 pb-3 space-y-0 bg-[hsl(223,80%,14%)] rounded-lg mx-1 mb-2 overflow-hidden"
                        style={{ pointerEvents: 'auto' }}
                      >
                        {link.children.map((child) => {
                          const IconComponent = child.icon === 'status' ? Activity : child.icon === 'help' ? HelpCircle : child.icon === 'docs' ? FileText : null;
                          
                          // External links
                          if (child.external) {
                            return (
                              <a
                                key={child.name}
                                href={child.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-muted-foreground hover:text-foreground active:text-primary transition-colors py-4 px-4 min-h-[52px] text-base touch-manipulation rounded-md hover:bg-[hsl(223,80%,18%)] active:bg-[hsl(223,80%,20%)]"
                                style={{ 
                                  WebkitTapHighlightColor: 'transparent',
                                  pointerEvents: 'auto',
                                  cursor: 'pointer'
                                }}
                                onClick={() => {
                                  setOpenDropdown(null);
                                  setIsMobileMenuOpen(false);
                                }}
                              >
                                {IconComponent && <IconComponent className="h-5 w-5" />}
                                {child.name}
                              </a>
                            );
                          }
                          
                          // Hash links
                          if (child.href.includes('#')) {
                            return (
                              <a
                                key={child.name}
                                href={child.href}
                                className="flex items-center gap-2 text-muted-foreground hover:text-foreground active:text-primary transition-colors py-4 px-4 min-h-[52px] text-base touch-manipulation rounded-md hover:bg-[hsl(223,80%,18%)] active:bg-[hsl(223,80%,20%)]"
                                style={{ 
                                  WebkitTapHighlightColor: 'transparent',
                                  pointerEvents: 'auto',
                                  cursor: 'pointer'
                                }}
                                onClick={() => {
                                  setOpenDropdown(null);
                                  setIsMobileMenuOpen(false);
                                }}
                              >
                                {IconComponent && <IconComponent className="h-5 w-5" />}
                                {child.name}
                              </a>
                            );
                          }
                          
                          // Internal links
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              className="flex items-center gap-2 text-muted-foreground hover:text-foreground active:text-primary transition-colors py-4 px-4 min-h-[52px] text-base touch-manipulation rounded-md hover:bg-[hsl(223,80%,18%)] active:bg-[hsl(223,80%,20%)]"
                              style={{ 
                                WebkitTapHighlightColor: 'transparent',
                                pointerEvents: 'auto',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                setOpenDropdown(null);
                                setIsMobileMenuOpen(false);
                              }}
                            >
                              {IconComponent && <IconComponent className="h-5 w-5" />}
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              if (link.href.startsWith("/") && !link.href.includes("#")) {
                return (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-muted-foreground hover:text-foreground transition-colors py-4 min-h-[52px] flex items-center text-base font-medium border-b border-border/30 last:border-b-0 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                );
              }

              return (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground transition-colors py-4 min-h-[52px] flex items-center text-base font-medium border-b border-border/30 last:border-b-0 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              );
            })}
            <div className="flex flex-col gap-2 pt-4 border-t border-border mt-2">
              <a href="https://portal.evgateway.com/#/login" target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" className="justify-start w-full min-h-[52px]">
                  Sign In
                </Button>
              </a>
              <a href="https://portal.evgateway.com/#/joinus" target="_blank" rel="noopener noreferrer">
                <Button variant="hero" className="w-full min-h-[52px]">Register</Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
    </>
  );
}